import { useEffect, useCallback } from 'react';
import ReactFlow, {
  addEdge,
  ConnectionLineType,
  useNodesState,
  useEdgesState,
} from 'reactflow';
import dagre from 'dagre';
import axiosInstance from '../../helpers/axios';
import 'reactflow/dist/style.css';

// --- Dagre layouting setup ---
const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

const getLayoutedElements = (nodes, edges, direction = 'LR') => {
  const isHorizontal = direction === 'LR';
  dagreGraph.setGraph({ rankdir: direction, ranksep: 150, nodesep: 10 });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: node.width, height: node.height });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  nodes.forEach((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    
    node.position = {
      x: nodeWithPosition.x - node.width / 2,
      y: nodeWithPosition.y - node.height / 2,
    };

    node.targetPosition = isHorizontal ? 'left' : 'top';
    node.sourcePosition = isHorizontal ? 'right' : 'bottom';

    return node;
  });

  return { nodes, edges };
};

// --- Main Component ---
const LayoutFlow = ({ appId, userId, shouldFetchData = true }) => {
  const user = null;
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const processApiResponse = (data) => {
    if (!data || data.length === 0) {
      return { nodes: [], edges: [] };
    }
  
    let nodes = [];
    let edges = [];
  
    const sourceNodeWidth = 180;
    const sourceNodeHeight = 60;
    const flagNodeWidth = 500;
    const flagNodeHeight = 70;
    const outputNodeWidth = 150;
    const outputNodeHeight = 60;

    // Process ALL items in the data array
    data.forEach((item, index) => {
      // Check if result structure exists for this item
      if (!item?.result?.result) {
        console.warn(`Skipping item ${index}: missing result.result`);
        return;
      }

      // Create a source node for each data item
      const sourceNodeId = `source-${index}`;
      nodes.push({
        id: sourceNodeId,
        data: { label: `${item.source || 'Source'}` },
        width: sourceNodeWidth,
        height: sourceNodeHeight,
        draggable: false,
        style: {
          width: `${sourceNodeWidth}px`,
          height: `${sourceNodeHeight}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      });

      // Get the overall color for this specific item
      const overallColor = item.result.result.overall || 'green';

      // Create a separate Overall node for this item
      const overallNodeId = `overall-${index}`;
      nodes.push({
        id: overallNodeId,
        data: { label: `Overall` },
        width: outputNodeWidth,
        height: outputNodeHeight,
        draggable: false,
        style: {
          width: `${outputNodeWidth}px`,
          height: `${outputNodeHeight}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderColor: overallColor === "green" ? "#12B76A" : overallColor === "orange" ? "#F8A31F" : "#D22129",
          color: overallColor === "green" ? "#12B76A" : overallColor === "orange" ? "#F8A31F" : "#D22129",
          backgroundColor: overallColor === "green" ? "#ECFDF3" : overallColor === "orange" ? "#FFF6E8" : "#FEEAEB",
          fontWeight: '600',
        },
      });

      // Get flags, inputs, and criteria for this item
      const flags = item.result.result.flag || {};
      const inputs = item.result.result.input || {};
      const criterias = item.result.result.criteria || {};
    
      // Create flag nodes for each flag in this item
      Object.keys(flags).forEach((key) => {
        const flagColor = flags[key] || 'green';
        const flagNodeId = `flag-${index}-${key.replace(/\s+/g, '-')}`;
        
        // Safely get criteria and input values
        const criteriaText = criterias[key] || key;
        const inputValue = inputs[key] !== undefined ? inputs[key] : '';
        
        const flagLabel = (
          <>
            <span style={{fontSize:'14px'}}>{criteriaText}</span> 
            <strong style={{ fontWeight:'bold', fontSize:'14px', marginLeft:'10px'}}>
              <span style={{color: flagColor === "green" ? "#067647" : flagColor === "orange" ? "#B54708" : "#D22129"}}>
                {inputValue !== '' ? ` (${inputValue})` : ''}
              </span>
            </strong>
          </>
        );
    
        // Create the flag node
        nodes.push({
          id: flagNodeId,
          data: { label: flagLabel },
          width: flagNodeWidth,
          height: flagNodeHeight,
          draggable: false,
          style: {
            width: `${flagNodeWidth}px`,
            height: `${flagNodeHeight}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            lineHeight: '1.4',
            borderColor: flagColor === "green" ? "#12B76A" : flagColor === "orange" ? "#F8A31F" : "#D22129",
            color: flagColor === "green" ? "#067647" : flagColor === "orange" ? "#B54708" : "#D22129",
            backgroundColor: flagColor === "green" ? "#ECFDF3" : flagColor === "orange" ? "#FFF6E8" : "#FEEAEB",
            fontWeight: '500',
          },
        });
    
        // Create edge from source -> flag node
        edges.push({ 
          id: `e-source-${index}-to-${flagNodeId}`, 
          source: sourceNodeId, 
          target: flagNodeId, 
          type: 'default',
          pathOptions: { curvature: 0.3 }
        });
    
        // Create edge from flag node -> this item's Overall node
        edges.push({ 
          id: `e-${flagNodeId}-to-output-${index}`, 
          source: flagNodeId, 
          target: overallNodeId,
          type: 'default',
          pathOptions: { curvature: 0.3 }
        });
      });
    });
  
    return { nodes, edges };
  };

  useEffect(() => {
    if (!shouldFetchData) return;  
    
    const fetchData = async () => {
      try {
        const url = `/go-rules/get-result?applicationId=${appId}&userId=${userId}`;
        const res = await axiosInstance.get(url, {
          headers: {
          },
        });
        
        const apiData = res?.data?.data;
        
        if (apiData) {
          const { nodes: processedNodes, edges: processedEdges } = processApiResponse(apiData);
          
          // Only proceed with layout if we have nodes
          if (processedNodes.length > 0) {
            const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(processedNodes, processedEdges);
            setNodes(layoutedNodes);
            setEdges(layoutedEdges);
          } else {
            console.warn("No nodes to display");
          }
        }
      } catch (error) {
        console.error("Failed to fetch or process flow data:", error);
      }
    };
  
    if (appId && userId) {
      fetchData();
    }
  }, [appId, userId, user, setNodes, setEdges, shouldFetchData]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ 
      ...params, 
      type: ConnectionLineType.SmoothStep, 
      animated: true 
    }, eds)),
    [setEdges]
  );

  return (
    <div style={{ width: '100%', height: '100vh' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        connectionLineType={ConnectionLineType.SmoothStep}
        fitView
        proOptions={{ hideAttribution: true }}
      />
    </div>
  );
};

export default LayoutFlow;